import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Search, ExternalLink, ShieldCheck, CheckCircle2, Circle, AlertTriangle, Loader2,
  UserCheck, FileCheck2, Paperclip, Send,
} from "lucide-react";

const REL_OPTIONS = [
  ["visitor", "Visitor"],
  ["registered_household", "Registered household"],
  ["advisor_prospect", "Advisor prospect"],
  ["active_client", "Active client"],
];
const REL_LABEL = Object.fromEntries(REL_OPTIONS);

const EVENT_META = {
  link_opened: { label: "Link opened", icon: ExternalLink, tone: "text-[#1E3E62]" },
  application_submitted: { label: "Application submitted", icon: FileCheck2, tone: "text-[#92400E]" },
  enrollment_confirmed: { label: "Enrollment confirmed", icon: ShieldCheck, tone: "text-[#166534]" },
};

export default function HouseholdEnrollment({ hid, onChanged }) {
  const [sum, setSum] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/agent/households/${hid}/enrollment`); setSum(data); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail?.message || "Could not load enrollment."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hid]);

  const refresh = async () => { await load(); onChanged && onChanged(); };

  if (err) return <div className="rounded-xl border-2 border-[#F3D6D6] bg-[#FDF3F3] p-6 text-[#991B1B] text-sm" data-testid="enrollment-error">{err}</div>;
  if (!sum) return <Skeleton className="h-96 w-full rounded-xl" />;

  return (
    <div className="space-y-5" data-testid="enrollment-tab">
      <WritingAgentAndRelationship sum={sum} hid={hid} refresh={refresh} />
      <ProgressStrip sum={sum} />
      <ResolvePanel sum={sum} hid={hid} refresh={refresh} />
      <RecordEvidence sum={sum} hid={hid} refresh={refresh} />
      <EventsTimeline sum={sum} />
    </div>
  );
}

function WritingAgentAndRelationship({ sum, hid, refresh }) {
  const [busy, setBusy] = useState(false);
  const wa = sum.writing_agent;

  const setRel = async (status, imported) => {
    setBusy(true);
    try {
      await api.patch(`/agent/households/${hid}/relationship`,
        { status, imported_sba_client: imported });
      await refresh();
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-5" data-testid="enrollment-header">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-[#9CA3AF] mb-1.5">Writing agent (routing identity)</p>
          {wa ? (
            <div className="flex items-center gap-2" data-testid="enrollment-writing-agent">
              <UserCheck className="w-5 h-5 text-[#0B192C]" />
              <div>
                <p className="font-semibold text-[#0B192C]">{wa.name}</p>
                <p className="text-xs text-[#6B7280]">NPN {wa.npn || "—"}</p>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border-2 border-[#FDE68A] bg-[#FFFBEB] p-3 flex items-start gap-2" data-testid="enrollment-no-agent">
              <AlertTriangle className="w-4 h-4 text-[#92400E] mt-0.5 shrink-0" />
              <p className="text-xs text-[#92400E]">No writing agent assigned. Assign one from the Roster / dashboard — routing always uses the assigned agent's own PURL, never another agent's.</p>
            </div>
          )}
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-[#9CA3AF] mb-1.5">Relationship <span className="normal-case font-normal">(separate from enrollment)</span></p>
          <div className="flex items-center gap-2">
            <select
              disabled={busy}
              value={sum.relationship_status_effective}
              onChange={(e) => setRel(e.target.value, sum.imported_sba_client)}
              className="h-9 rounded-md border bg-white px-2 text-sm font-semibold text-[#0B192C]"
              data-testid="relationship-select">
              {REL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {sum.relationship_status_source === "derived" && (
              <span className="text-[10px] text-[#9CA3AF]" data-testid="relationship-derived">derived — set to confirm</span>
            )}
          </div>
          <label className="flex items-center gap-2 mt-2 text-xs text-[#374151] cursor-pointer" data-testid="imported-client-label">
            <input type="checkbox" disabled={busy} checked={!!sum.imported_sba_client}
              onChange={(e) => setRel(sum.relationship_status_effective === "visitor" ? "active_client" : sum.relationship_status_effective, e.target.checked)}
              data-testid="imported-client-checkbox" />
            Imported existing SBA client (a client even without a Nauticus enrollment)
          </label>
        </div>
      </div>
    </div>
  );
}

function ProgressStrip({ sum }) {
  const s = sum.enrollment_state;
  const steps = [
    ["link_opened", "Link opened", s.link_opened],
    ["application_submitted", "Application submitted", s.application_submitted],
    ["enrollment_confirmed", "Carrier confirmed", s.enrollment_confirmed],
  ];
  return (
    <div className="rounded-xl border-2 border-[#E5E5E0] bg-[#FBFBF9] p-4" data-testid="enrollment-progress">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {steps.map(([k, label, done]) => (
          <div key={k} className="flex items-center gap-2" data-testid={`progress-${k}`}>
            {done ? <CheckCircle2 className="w-5 h-5 text-[#166534]" /> : <Circle className="w-5 h-5 text-[#CBD5E1]" />}
            <span className={`text-sm font-semibold ${done ? "text-[#0B192C]" : "text-[#9CA3AF]"}`}>{label}</span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-[#6B7280] mt-2.5">A SunFire submission confirmation documents that an application was submitted — it does <b>not</b> establish that the carrier accepted or effectuated coverage. Carrier-confirmed enrollment is recorded separately with its own evidence.</p>
    </div>
  );
}

function ResolvePanel({ sum, hid, refresh }) {
  const [f, setF] = useState({ plan_year: 2026, org: "", plan_name: "", contract_id: "", pbp: "", segment_id: "", county: "", state: "" });
  const [results, setResults] = useState(null);
  const [sel, setSel] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  const search = async () => {
    setBusy("search"); setMsg(null); setResults(null);
    try {
      const { data } = await api.get("/agent/plan-search", { params: { plan_year: f.plan_year, org: f.org, plan_name: f.plan_name, contract_id: f.contract_id, pbp: f.pbp, segment_id: f.segment_id, county: f.county, state: f.state } });
      setResults(data.results || []);
    } catch (e) { setMsg(e?.response?.data?.detail || "Search failed."); }
    finally { setBusy(""); }
  };

  const resolve = async (plan) => {
    setSel(plan); setRes(null); setBusy("resolve"); setMsg(null);
    try {
      const { data } = await api.post(`/agent/households/${hid}/enrollment/resolve`, {
        plan_year: plan.plan_year, contract_id: plan.contract_id, pbp: plan.pbp, segment_id: plan.segment_id,
        state: f.state || undefined, county: f.county || undefined,
      });
      setRes(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setMsg(typeof d === "string" ? d : (d?.message || "Could not resolve route."));
    } finally { setBusy(""); }
  };

  const openLink = async () => {
    if (!res?.destination?.url) return;
    window.open(res.destination.url, "_blank", "noopener,noreferrer");
    setBusy("open");
    try {
      await api.post(`/agent/households/${hid}/enrollment/events`, {
        event_type: "link_opened",
        plan_year: res.plan.plan_year, contract_id: res.plan.contract_id, pbp: res.plan.pbp, segment_id: res.plan.segment_id,
        plan_name: res.plan.plan_name, carrier: res.plan.carrier,
        provider_code: res.provider?.code, fmo_id: res.fmo_id,
        destination_url: res.destination.url, landing_type: res.landing_type,
      });
      await refresh();
    } finally { setBusy(""); }
  };

  const isExact = res?.landing_type === "exact_plan_link";

  return (
    <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-5" data-testid="resolve-panel">
      <h3 className="font-bold text-[#0B192C] mb-1">Resolve enrollment route</h3>
      <p className="text-xs text-[#6B7280] mb-4">Pick the exact plan/year, then resolve the assigned writing agent's eligible SunFire destination. Selection — not matching — establishes the exact plan.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <div><Label className="text-xs">Plan year</Label><Input type="number" value={f.plan_year} onChange={(e) => setF({ ...f, plan_year: e.target.value })} className="h-9 mt-1" data-testid="resolve-year" /></div>
        <div className="col-span-2"><Label className="text-xs">Carrier / org</Label><Input value={f.org} onChange={(e) => setF({ ...f, org: e.target.value })} placeholder="e.g. Aetna" className="h-9 mt-1" data-testid="resolve-org" /></div>
        <div><Label className="text-xs">State</Label><Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} placeholder="FL" className="h-9 mt-1" data-testid="resolve-state" /></div>
        <div><Label className="text-xs">Contract</Label><Input value={f.contract_id} onChange={(e) => setF({ ...f, contract_id: e.target.value })} placeholder="H0523" className="h-9 mt-1" data-testid="resolve-contract" /></div>
        <div><Label className="text-xs">PBP</Label><Input value={f.pbp} onChange={(e) => setF({ ...f, pbp: e.target.value })} placeholder="022" className="h-9 mt-1" data-testid="resolve-pbp" /></div>
        <div><Label className="text-xs">Segment</Label><Input value={f.segment_id} onChange={(e) => setF({ ...f, segment_id: e.target.value })} placeholder="000" className="h-9 mt-1" data-testid="resolve-segment" /></div>
        <div><Label className="text-xs">County</Label><Input value={f.county} onChange={(e) => setF({ ...f, county: e.target.value })} className="h-9 mt-1" data-testid="resolve-county" /></div>
      </div>
      <Button onClick={search} disabled={busy === "search"} className="bg-[#0B192C] text-white" data-testid="resolve-search-btn">
        {busy === "search" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" />Find exact plan</>}
      </Button>

      {msg && <p className="text-sm text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3 mt-3" data-testid="resolve-msg">{msg}</p>}

      {results && (
        <div className="mt-3 rounded-lg border-2 border-[#E5E5E0] divide-y max-h-64 overflow-y-auto" data-testid="resolve-results">
          {results.length === 0 && <p className="p-3 text-sm text-[#6B7280]">No plans matched.</p>}
          {results.map((p) => (
            <div key={p.display_id} className={`flex items-center gap-3 p-3 ${sel?.display_id === p.display_id ? "bg-[#F0F7F4]" : ""}`} data-testid={`resolve-plan-${p.display_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0B192C] truncate">{p.plan_name}</p>
                <p className="text-xs font-mono text-[#1E3E62]">{p.display_id} · {p.organization_marketing_name} · {p.plan_year}</p>
              </div>
              <Button size="sm" variant="outline" disabled={busy === "resolve"} onClick={() => resolve(p)} data-testid={`resolve-select-${p.display_id}`}>Resolve</Button>
            </div>
          ))}
        </div>
      )}

      {res && res.resolved && (
        <div className="mt-4 rounded-xl border-2 border-[#CDE7D8] bg-[#F0F7F4] p-4" data-testid="resolve-result">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#0B192C] text-[#D4AF37]">{res.provider?.name}</span>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${isExact ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]"}`} data-testid="resolve-landing-badge">
              {isExact ? "Exact-plan link" : "General marketplace"}
            </span>
          </div>
          <p className="text-sm font-semibold text-[#0B192C]">{res.destination?.label}</p>
          {res.destination?.description && <p className="text-xs text-[#6B7280] mt-1">{res.destination.description}</p>}
          <p className="text-[11px] text-[#6B7280] mt-2">Routing via {res.writing_agent?.name} · NPN {res.writing_agent?.npn}. {res.note}</p>
          <Button onClick={openLink} disabled={busy === "open"} className="mt-3 h-10 bg-[#0B192C] text-white" data-testid="resolve-open-link">
            {busy === "open" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-1.5" />Open enrollment link (records "link opened")</>}
          </Button>
        </div>
      )}
      {res && !res.resolved && (
        <div className="mt-4 rounded-xl border-2 border-[#FDE68A] bg-[#FFFBEB] p-4 text-sm text-[#92400E]" data-testid="resolve-unresolved">{res.message}</div>
      )}
    </div>
  );
}

function RecordEvidence({ sum, hid, refresh }) {
  const [open, setOpen] = useState(null); // 'application_submitted' | 'enrollment_confirmed'
  const lastPlan = (sum.events.find((e) => e.plan) || {}).plan || null;
  return (
    <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-5" data-testid="record-evidence">
      <h3 className="font-bold text-[#0B192C] mb-1">Record evidence</h3>
      <p className="text-xs text-[#6B7280] mb-4">Attach the SunFire confirmation / application PDF and record what actually happened. Each event is separate and requires its own evidence.</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setOpen("application_submitted")} data-testid="btn-record-submitted"><FileCheck2 className="w-4 h-4 mr-1.5" />Record application submitted</Button>
        <Button variant="outline" onClick={() => setOpen("enrollment_confirmed")} data-testid="btn-record-confirmed"><ShieldCheck className="w-4 h-4 mr-1.5" />Record carrier-confirmed enrollment</Button>
      </div>
      <EvidenceDialog open={!!open} type={open} onOpenChange={(v) => !v && setOpen(null)} hid={hid} lastPlan={lastPlan} onSaved={refresh} />
    </div>
  );
}

function EvidenceDialog({ open, type, onOpenChange, hid, lastPlan, onSaved }) {
  const isConfirm = type === "enrollment_confirmed";
  const [f, setF] = useState({ confirmation_number: "", evidence_source: "", evidence_date: "", note: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setF({ confirmation_number: "", evidence_date: "", note: "",
        evidence_source: isConfirm ? "Carrier confirmation" : "SunFire submission confirmation" });
      setFile(null); setErr(null);
    }
  }, [open, isConfirm]);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      let document_id = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file); fd.append("category", "enrollment");
        const { data: doc } = await api.post(`/agent/households/${hid}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        document_id = doc.id;
      }
      await api.post(`/agent/households/${hid}/enrollment/events`, {
        event_type: type,
        plan_year: lastPlan?.plan_year, contract_id: lastPlan?.contract_id, pbp: lastPlan?.pbp,
        segment_id: lastPlan?.segment_id, plan_name: lastPlan?.plan_name, carrier: lastPlan?.carrier,
        confirmation_number: f.confirmation_number || undefined,
        evidence_source: f.evidence_source || undefined,
        evidence_date: f.evidence_date || undefined,
        note: f.note || undefined, document_id: document_id || undefined,
      });
      await onSaved(); onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || "Could not save. Provide the required evidence."));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="evidence-dialog">
        <DialogHeader>
          <DialogTitle>{isConfirm ? "Record carrier-confirmed enrollment" : "Record application submitted"}</DialogTitle>
          <DialogDescription>
            {isConfirm
              ? "Only record this when carrier evidence supports that coverage was accepted/effectuated. Retain its source and date."
              : "Documents that an application was submitted (SunFire confirmation + PDF). This does not establish that the carrier accepted coverage."}
          </DialogDescription>
        </DialogHeader>
        {lastPlan
          ? <p className="text-xs text-[#6B7280]">Plan: <b>{lastPlan.plan_name || lastPlan.display_id}</b> · {lastPlan.display_id}</p>
          : <p className="text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded p-2">No plan resolved yet — resolve a route first so the plan identity is attached.</p>}
        <div className="grid gap-3 mt-2">
          <div><Label className="text-xs">Confirmation number</Label><Input value={f.confirmation_number} onChange={(e) => setF({ ...f, confirmation_number: e.target.value })} placeholder="e.g. SF-123456" className="h-9 mt-1" data-testid="evidence-confirmation" /></div>
          <div><Label className="text-xs">Evidence source</Label><Input value={f.evidence_source} onChange={(e) => setF({ ...f, evidence_source: e.target.value })} className="h-9 mt-1" data-testid="evidence-source" /></div>
          <div><Label className="text-xs">Evidence date</Label><Input type="date" value={f.evidence_date} onChange={(e) => setF({ ...f, evidence_date: e.target.value })} className="h-9 mt-1" data-testid="evidence-date" /></div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" />Attach document (confirmation / application PDF)</Label>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 text-sm" data-testid="evidence-file" />
          </div>
          <div><Label className="text-xs">Note</Label><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1" rows={2} data-testid="evidence-note" /></div>
        </div>
        {err && <p className="text-sm text-rose-600" data-testid="evidence-error">{err}</p>}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="evidence-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !lastPlan} className="bg-[#0B192C] text-white" data-testid="evidence-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Record event</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EventsTimeline({ sum }) {
  if (!sum.events.length) return (
    <div className="rounded-xl border-2 border-dashed border-[#E5E5E0] bg-white p-6 text-center text-sm text-[#9CA3AF]" data-testid="events-empty">No enrollment events recorded yet.</div>
  );
  return (
    <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-5" data-testid="events-timeline">
      <h3 className="font-bold text-[#0B192C] mb-3">Enrollment events</h3>
      <div className="space-y-3">
        {sum.events.map((e) => {
          const meta = EVENT_META[e.event_type] || EVENT_META.link_opened;
          const Icon = meta.icon;
          return (
            <div key={e.id} className="flex gap-3 border-b last:border-0 pb-3 last:pb-0" data-testid={`event-${e.id}`}>
              <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${meta.tone}`} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-[#0B192C]">{meta.label}</p>
                  {e.carrier_verified
                    ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]">Carrier-verified</span>
                    : e.event_type === "application_submitted" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">Advisor-attested</span>}
                </div>
                {e.plan && <p className="text-xs text-[#6B7280]">{e.plan.plan_name || e.plan.display_id} · {e.plan.display_id}</p>}
                <p className="text-[11px] text-[#6B7280] mt-0.5">
                  {e.writing_agent?.name && <>NPN {e.writing_agent.npn} · </>}
                  {e.confirmation_number && <>Conf# {e.confirmation_number} · </>}
                  {e.evidence_source && <>{e.evidence_source} · </>}
                  {e.evidence_date && <>{e.evidence_date} · </>}
                  {(e.created_at || "").slice(0, 10)}
                </p>
                {e.note && <p className="text-xs text-[#374151] mt-1 italic">{e.note}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
